'use client'

import React, { useCallback, useRef, useState } from 'react'

import { useEscapeKey } from '../../hooks/useEscapeKey'
import RDatePicker from 'react-datepicker'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { fieldLabelWrapperStyles } from '../shared/fieldLabelStyles'
import {
  datePickerLabelStyles,
  datePickerWrapperStyles,
} from './DatePicker.styles'

export type DatePickerProps = {
  /** Currently selected date; null renders the placeholder. */
  date: Date | null
  onChange: (date: Date) => void
  /**
   * Called when the field is cleared (the date removed via the text input or a
   * clear control). `onChange` only fires for real dates, so an optional field
   * needs this to represent the empty value.
   */
  onClear?: () => void
  minDate?: Date | null
  maxDate?: Date | null
  /**
   * Optional label rendered above the input, in the Selection field's
   * uppercase eyebrow (Overline) style per the Date Picker spec.
   */
  label?: string
  placeholder?: string
  isDisabled?: boolean
  /**
   * Display + parse format string (react-datepicker / date-fns tokens).
   * Defaults to the calendar-first `'MMM d, yyyy'`. Pass `'MM/dd/yyyy'` for a
   * text-entry-first field such as DOB — the input then reads and accepts
   * `mm/dd/yyyy` typed values while the calendar stays available. Pair it with
   * `placeholder='mm/dd/yyyy'`.
   */
  dateFormat?: string
  colorScheme?: ColorScheme
  /** Optional semantic token used for the input's resting border color. */
  inputBorderColor?: string
  /**
   * Where the calendar opens relative to the input. Use 'top' variants when
   * the input sits near the bottom of a scrolling container.
   */
  popperPlacement?: 'bottom' | 'bottom-start' | 'top' | 'top-start'
  /** Show a time column alongside the calendar. */
  showTimeSelect?: boolean
  /** Render a time-only picker (no calendar). Pair with `showTimeSelect`. */
  showTimeSelectOnly?: boolean
  /** Minutes between selectable times when a time column is shown. Default 15. */
  timeIntervals?: number
  /** Heading above the time column. */
  timeCaption?: string
  'data-testid'?: string
}

/**
 * Dark-themed calendar input wrapping the `react-datepicker` library (the
 * dependency the legacy DateSelect already uses). The library's base
 * stylesheet must be loaded by the host app (web-app imports it in _app.tsx;
 * storybook imports it in preview.tsx) - this component only themes it.
 */
export const DatePicker = ({
  date,
  onChange,
  onClear,
  minDate,
  maxDate,
  label,
  placeholder,
  isDisabled = false,
  dateFormat = 'MMM d, yyyy',
  colorScheme = 'dark',
  inputBorderColor,
  popperPlacement = 'bottom-start',
  showTimeSelect = false,
  showTimeSelectOnly = false,
  timeIntervals = 15,
  timeCaption,
  'data-testid': dataTestId,
}: DatePickerProps) => {
  const pickerRef = useRef<RDatePicker>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  // Own Escape while the calendar is open, so it closes the calendar before
  // any surrounding Drawer or Modal.
  const closeCalendar = useCallback(() => {
    pickerRef.current?.setOpen(false)
  }, [])
  useEscapeKey(closeCalendar, isCalendarOpen)

  const picker = (
    <Box
      css={datePickerWrapperStyles(colorScheme, isDisabled, inputBorderColor)}
      data-testid={dataTestId}
    >
      <RDatePicker
        dateFormat={dateFormat}
        disabled={isDisabled}
        maxDate={maxDate}
        minDate={minDate}
        onCalendarClose={() => setIsCalendarOpen(false)}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        onChange={(value: Date | null) => {
          if (value) onChange(value)
          else onClear?.()
        }}
        placeholderText={placeholder}
        popperPlacement={popperPlacement}
        ref={pickerRef}
        selected={date}
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        timeIntervals={timeIntervals}
        {...(timeCaption ? { timeCaption } : {})}
      />
    </Box>
  )

  if (!label) return picker

  return (
    <Box {...fieldLabelWrapperStyles()}>
      <Box as='label' {...datePickerLabelStyles(colorScheme)}>
        {label}
      </Box>
      {picker}
    </Box>
  )
}
